import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as d3 from 'd3';
import { 
  Flame, 
  TrendingUp, 
  TrendingDown, 
  Activity, 
  ArrowRight, 
  RefreshCw, 
  Info, 
  ShieldAlert, 
  Zap, 
  Download,
  Filter,
  Layers,
  BarChart2,
  MoveHorizontal
} from 'lucide-react';
import { fxEngine, financialFormat } from '../../services/fxEngine';
import { CURRENCIES_BY_CODE } from '../../data/fxCurrenciesData';

export interface MarketHeatmapProps {
  onSelectPair?: (base: string, quote: string) => void;
  className?: string;
}

type HeatmapMetric = 'volatility' | 'change' | 'spread';
type CurrencyBasket = 'g8' | 'top10' | 'emerging';

interface PairMatrixCell {
  base: string;
  quote: string;
  rate: number;
  high24h: number;
  low24h: number;
  change24hPct: number;
  volatilityPct: number;
  spreadBps: number;
  isDiagonal: boolean;
  volatilityRating: 'Low' | 'Moderate' | 'Elevated' | 'High';
}

const BASKET_CURRENCIES: Record<CurrencyBasket, string[]> = {
  g8: ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'NZD'],
  top10: ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF', 'CNY', 'INR', 'SGD'],
  emerging: ['USD', 'EUR', 'GBP', 'JPY', 'BRL', 'MXN', 'ZAR', 'INR', 'CNY', 'AED'],
};

export const MarketHeatmap: React.FC<MarketHeatmapProps> = ({
  onSelectPair,
  className = '',
}) => {
  const [basket, setBasket] = useState<CurrencyBasket>('g8');
  const [metric, setMetric] = useState<HeatmapMetric>('volatility');
  const [hoveredCell, setHoveredCell] = useState<PairMatrixCell | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ x: number; y: number } | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date>(new Date());

  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const currencies = BASKET_CURRENCIES[basket];

  // Generate Matrix Data
  const matrixData = useMemo(() => {
    const list: PairMatrixCell[] = [];

    currencies.forEach((base) => {
      currencies.forEach((quote) => {
        if (base === quote) {
          list.push({
            base,
            quote,
            rate: 1.0,
            high24h: 1.0,
            low24h: 1.0,
            change24hPct: 0.0,
            volatilityPct: 0.0,
            spreadBps: 0.0,
            isDiagonal: true,
            volatilityRating: 'Low',
          });
          return;
        }

        const obs = fxEngine.getPairObservation(base, quote);
        const rate = obs.rate;
        const high = obs.high24h || rate * 1.004;
        const low = obs.low24h || rate * 0.996;
        const change = obs.change24hPct;
        
        // Daily Volatility Amplitude (% intraday movement)
        const volatilityPct = Math.max(0.05, Math.abs(((high - low) / rate) * 100));
        
        // Spread estimation based on currency tier
        const isMajorBase = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD'].includes(base);
        const isMajorQuote = ['USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD'].includes(quote);
        const spreadBps = isMajorBase && isMajorQuote ? 1.2 : (!isMajorBase && !isMajorQuote ? 9.5 : 4.2);

        let volatilityRating: 'Low' | 'Moderate' | 'Elevated' | 'High' = 'Low';
        if (volatilityPct >= 1.4) volatilityRating = 'High';
        else if (volatilityPct >= 0.9) volatilityRating = 'Elevated';
        else if (volatilityPct >= 0.45) volatilityRating = 'Moderate';

        list.push({
          base,
          quote,
          rate,
          high24h: high,
          low24h: low,
          change24hPct: change,
          volatilityPct,
          spreadBps,
          isDiagonal: false,
          volatilityRating,
        });
      });
    });

    return list;
  }, [currencies, lastRefreshed]);

  // Key Analytics & Highlights
  const stats = useMemo(() => {
    const nonDiagonal = matrixData.filter((c) => !c.isDiagonal);
    if (nonDiagonal.length === 0) {
      return {
        highestVol: null,
        lowestVol: null,
        avgVol: 0,
        regime: 'Normal',
      };
    }

    const highestVol = [...nonDiagonal].sort((a, b) => b.volatilityPct - a.volatilityPct)[0];
    const lowestVol = [...nonDiagonal].sort((a, b) => a.volatilityPct - b.volatilityPct)[0];
    const avgVol = nonDiagonal.reduce((acc, c) => acc + c.volatilityPct, 0) / nonDiagonal.length;

    let regime = 'Normal';
    if (avgVol > 1.1) regime = 'Elevated Volatility (Risk-Off / Breakout)';
    else if (avgVol < 0.4) regime = 'Low Volatility (Consolidation)';
    else regime = 'Stable Equilibrium (Normal)';

    return { highestVol, lowestVol, avgVol, regime };
  }, [matrixData]);

  // Render D3 Heatmap
  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    const containerWidth = containerRef.current.clientWidth || 700;
    const margin = { top: 44, right: 24, bottom: 44, left: 60 };
    const width = Math.max(containerWidth - margin.left - margin.right, 340);
    const height = Math.max(Math.min(width * 0.82, 540), 360);

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    svg
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom);

    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // X and Y Band Scales
    const xScale = d3
      .scaleBand()
      .domain(currencies)
      .range([0, width])
      .padding(0.08);

    const yScale = d3
      .scaleBand()
      .domain(currencies)
      .range([0, height])
      .padding(0.08);

    // Color Scales
    let colorScale: (val: number) => string;

    if (metric === 'volatility') {
      const maxVol = Math.max(...matrixData.map((d) => d.volatilityPct), 2.0);
      // Soft Emerald (Calm) -> Amber (Active) -> Crimson (High Volatility)
      colorScale = d3
        .scaleLinear<string>()
        .domain([0, 0.45, 1.0, maxVol])
        .range(['#F0FDF4', '#FEF3C7', '#F97316', '#DC2626'])
        .interpolate(d3.interpolateRgb);
    } else if (metric === 'change') {
      const maxChange = Math.max(
        ...matrixData.map((d) => Math.abs(d.change24hPct)),
        1.2
      );
      // Bearish Red -> Neutral Slate -> Bullish Green
      colorScale = d3
        .scaleLinear<string>()
        .domain([-maxChange, -0.2, 0, 0.2, maxChange])
        .range(['#EF4444', '#FCA5A5', '#F8FAFC', '#86EFAC', '#16A34A'])
        .interpolate(d3.interpolateRgb);
    } else {
      // Spread BPS scale: Low (Emerald) -> High (Violet)
      const maxSpread = Math.max(...matrixData.map((d) => d.spreadBps), 10);
      colorScale = d3
        .scaleLinear<string>()
        .domain([0, 2.5, 6, maxSpread])
        .range(['#ECFDF5', '#D1FAE5', '#C7D2FE', '#6366F1'])
        .interpolate(d3.interpolateRgb);
    }

    // Top X-Axis (Quote Currencies)
    const xAxisTop = d3.axisTop(xScale).tickSize(0);
    const xGroup = g.append('g').call(xAxisTop);
    xGroup.select('.domain').remove();
    xGroup
      .selectAll('text')
      .style('font-size', '11px')
      .style('font-weight', '700')
      .style('fill', '#1A1A1A')
      .style('font-family', 'ui-monospace, SFMono-Regular, monospace');

    // Left Y-Axis (Base Currencies)
    const yAxisLeft = d3.axisLeft(yScale).tickSize(0);
    const yGroup = g.append('g').call(yAxisLeft);
    yGroup.select('.domain').remove();
    yGroup
      .selectAll('text')
      .style('font-size', '11px')
      .style('font-weight', '700')
      .style('fill', '#1A1A1A')
      .style('font-family', 'ui-monospace, SFMono-Regular, monospace');

    // Axis Labels
    g.append('text')
      .attr('x', width / 2)
      .attr('y', -24)
      .attr('text-anchor', 'middle')
      .style('font-size', '10px')
      .style('font-weight', '600')
      .style('fill', '#6B7280')
      .style('letter-spacing', '0.05em')
      .text('QUOTE CURRENCY (TO) →');

    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', -42)
      .attr('text-anchor', 'middle')
      .style('font-size', '10px')
      .style('font-weight', '600')
      .style('fill', '#6B7280')
      .style('letter-spacing', '0.05em')
      .text('← BASE CURRENCY (FROM)');

    // Cell Groups
    const cellGroups = g
      .selectAll<SVGGElement, PairMatrixCell>('.cell')
      .data(matrixData)
      .enter()
      .append('g')
      .attr('class', 'cell')
      .attr('transform', (d: PairMatrixCell) => `translate(${xScale(d.quote) || 0},${yScale(d.base) || 0})`)
      .style('cursor', (d: PairMatrixCell) => (d.isDiagonal ? 'default' : 'pointer'));

    // Rectangles
    cellGroups
      .append('rect')
      .attr('width', xScale.bandwidth())
      .attr('height', yScale.bandwidth())
      .attr('rx', 4)
      .attr('ry', 4)
      .attr('fill', (d: PairMatrixCell) => {
        if (d.isDiagonal) return '#F3F4F6';
        if (metric === 'volatility') return colorScale(d.volatilityPct);
        if (metric === 'change') return colorScale(d.change24hPct);
        return colorScale(d.spreadBps);
      })
      .attr('stroke', '#E5E7EB')
      .attr('stroke-width', 1)
      .on('mouseenter', function (event: any, d: PairMatrixCell) {
        if (d.isDiagonal) return;
        d3.select(this)
          .attr('stroke', '#1A1A1A')
          .attr('stroke-width', 2)
          .raise();

        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          setTooltipPos({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
          });
        }
        setHoveredCell(d);
      })
      .on('mousemove', function (event: any, d: PairMatrixCell) {
        if (d.isDiagonal) return;
        const rect = containerRef.current?.getBoundingClientRect();
        if (rect) {
          setTooltipPos({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
          });
        }
      })
      .on('mouseleave', function () {
        d3.select(this)
          .attr('stroke', '#E5E7EB')
          .attr('stroke-width', 1);
        setHoveredCell(null);
      })
      .on('click', (event: any, d: PairMatrixCell) => {
        if (d.isDiagonal) return;
        if (onSelectPair) {
          onSelectPair(d.base, d.quote);
        }
      });

    // Cell Text Labels (values)
    cellGroups
      .append('text')
      .attr('x', xScale.bandwidth() / 2)
      .attr('y', yScale.bandwidth() / 2 + 3.5)
      .attr('text-anchor', 'middle')
      .style('font-size', xScale.bandwidth() > 42 ? '10px' : '9px')
      .style('font-weight', '700')
      .style('font-family', 'ui-monospace, SFMono-Regular, monospace')
      .style('fill', (d: PairMatrixCell) => {
        if (d.isDiagonal) return '#9CA3AF';
        if (metric === 'volatility') {
          return d.volatilityPct > 1.2 ? '#FFFFFF' : '#1A1A1A';
        }
        if (metric === 'change') {
          return Math.abs(d.change24hPct) > 0.8 ? '#FFFFFF' : '#1A1A1A';
        }
        return d.spreadBps > 6 ? '#FFFFFF' : '#1A1A1A';
      })
      .style('pointer-events', 'none')
      .text((d: PairMatrixCell) => {
        if (d.isDiagonal) return '—';
        if (metric === 'volatility') return `${d.volatilityPct.toFixed(2)}%`;
        if (metric === 'change') {
          const sign = d.change24hPct > 0 ? '+' : '';
          return `${sign}${d.change24hPct.toFixed(2)}%`;
        }
        return `${d.spreadBps.toFixed(1)}bp`;
      });
  }, [matrixData, metric, currencies]);

  // Export Matrix Data to CSV
  const handleExportCSV = () => {
    let csv = `Base,Quote,Rate,24h_Volatility_Pct,24h_Change_Pct,Spread_bps,Volatility_Rating\n`;
    matrixData.forEach((row) => {
      csv += `${row.base},${row.quote},${row.rate},${row.volatilityPct.toFixed(4)},${row.change24hPct.toFixed(4)},${row.spreadBps},${row.volatilityRating}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `yamoopay-fx-market-heatmap-${basket}-${metric}-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Top Header & Analytics Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        {/* KPI 1: Highest Volatility Pair */}
        <div className="p-4 bg-white border border-[#E5E7EB] rounded-xl space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280] flex items-center gap-1.5">
            <Flame className="w-3.5 h-3.5 text-rose-500" />
            <span>Highest Daily Volatility</span>
          </div>
          {stats.highestVol ? (
            <div>
              <div className="text-base font-extrabold text-[#1A1A1A] font-mono">
                {stats.highestVol.base} / {stats.highestVol.quote}
              </div>
              <div className="text-xs font-semibold text-rose-600 flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                <span>{stats.highestVol.volatilityPct.toFixed(2)}% Intraday Range</span>
              </div>
            </div>
          ) : (
            <div className="text-xs text-[#9CA3AF]">Calculating...</div>
          )}
        </div>

        {/* KPI 2: Lowest Volatility Anchor */}
        <div className="p-4 bg-white border border-[#E5E7EB] rounded-xl space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280] flex items-center gap-1.5">
            <Activity className="w-3.5 h-3.5 text-emerald-600" />
            <span>Most Stable Major Pair</span>
          </div>
          {stats.lowestVol ? (
            <div>
              <div className="text-base font-extrabold text-[#1A1A1A] font-mono">
                {stats.lowestVol.base} / {stats.lowestVol.quote}
              </div>
              <div className="text-xs font-semibold text-emerald-700 flex items-center gap-1">
                <span>{stats.lowestVol.volatilityPct.toFixed(2)}% Volatility</span>
                <span className="text-[10px] font-normal text-[#6B7280]">(Tightest band)</span>
              </div>
            </div>
          ) : (
            <div className="text-xs text-[#9CA3AF]">Calculating...</div>
          )}
        </div>

        {/* KPI 3: Basket Average Volatility */}
        <div className="p-4 bg-white border border-[#E5E7EB] rounded-xl space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280] flex items-center gap-1.5">
            <BarChart2 className="w-3.5 h-3.5 text-[#2563EB]" />
            <span>Basket Avg Volatility</span>
          </div>
          <div className="text-base font-extrabold text-[#1A1A1A] font-mono">
            {stats.avgVol.toFixed(2)}%
          </div>
          <div className="text-xs text-[#6B7280]">
            Across {currencies.length * (currencies.length - 1)} active pairs
          </div>
        </div>

        {/* KPI 4: Market Regime */}
        <div className="p-4 bg-white border border-[#E5E7EB] rounded-xl space-y-1">
          <div className="text-[10px] font-bold uppercase tracking-wider text-[#6B7280] flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5 text-amber-500" />
            <span>FX Market Regime</span>
          </div>
          <div className="text-xs font-bold text-[#1A1A1A] line-clamp-1">
            {stats.regime}
          </div>
          <div className="text-[10px] text-[#6B7280]">
            Realized intraday amplitude
          </div>
        </div>
      </div>

      {/* Heatmap Card */}
      <div className="p-6 bg-white border border-[#E5E7EB] rounded-2xl shadow-xs space-y-5">
        {/* Controls Toolbar */}
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 pb-4 border-b border-[#F3F4F6]">
          {/* Left: Title and Legend */}
          <div>
            <div className="flex items-center gap-2">
              <h3 className="text-base font-bold text-[#1A1A1A]">
                FX Market Volatility Heatmap
              </h3>
              <span className="px-2 py-0.5 bg-[#EFF6FF] text-[#2563EB] text-[10px] font-mono font-bold rounded-md border border-[#DBEAFE]">
                D3 Real-Time Matrix
              </span>
            </div>
            <p className="text-xs text-[#6B7280]">
              Visualized cross-currency volatility and daily price variance. Click any pair cell to load it into the converter.
            </p>
          </div>

          {/* Right: Metric Selector & Basket Switcher */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Basket Switcher */}
            <div className="flex items-center bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-0.5 text-xs">
              <button
                onClick={() => setBasket('g8')}
                className={`px-2.5 py-1 rounded-md font-semibold transition-colors cursor-pointer ${
                  basket === 'g8'
                    ? 'bg-white text-[#1A1A1A] shadow-2xs'
                    : 'text-[#6B7280] hover:text-[#1A1A1A]'
                }`}
              >
                G8 Majors
              </button>
              <button
                onClick={() => setBasket('top10')}
                className={`px-2.5 py-1 rounded-md font-semibold transition-colors cursor-pointer ${
                  basket === 'top10'
                    ? 'bg-white text-[#1A1A1A] shadow-2xs'
                    : 'text-[#6B7280] hover:text-[#1A1A1A]'
                }`}
              >
                Global Top 10
              </button>
              <button
                onClick={() => setBasket('emerging')}
                className={`px-2.5 py-1 rounded-md font-semibold transition-colors cursor-pointer ${
                  basket === 'emerging'
                    ? 'bg-white text-[#1A1A1A] shadow-2xs'
                    : 'text-[#6B7280] hover:text-[#1A1A1A]'
                }`}
              >
                High Vol / Emerging
              </button>
            </div>

            {/* Metric Mode */}
            <div className="flex items-center bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-0.5 text-xs">
              <button
                onClick={() => setMetric('volatility')}
                className={`px-2.5 py-1 rounded-md font-semibold transition-colors cursor-pointer ${
                  metric === 'volatility'
                    ? 'bg-white text-[#1A1A1A] shadow-2xs'
                    : 'text-[#6B7280] hover:text-[#1A1A1A]'
                }`}
                title="Daily Price Amplitude ((High - Low) / Rate)"
              >
                Volatility %
              </button>
              <button
                onClick={() => setMetric('change')}
                className={`px-2.5 py-1 rounded-md font-semibold transition-colors cursor-pointer ${
                  metric === 'change'
                    ? 'bg-white text-[#1A1A1A] shadow-2xs'
                    : 'text-[#6B7280] hover:text-[#1A1A1A]'
                }`}
                title="24-Hour Net % Change"
              >
                24h Change %
              </button>
              <button
                onClick={() => setMetric('spread')}
                className={`px-2.5 py-1 rounded-md font-semibold transition-colors cursor-pointer ${
                  metric === 'spread'
                    ? 'bg-white text-[#1A1A1A] shadow-2xs'
                    : 'text-[#6B7280] hover:text-[#1A1A1A]'
                }`}
                title="Institutional Spread Variance"
              >
                Spread (bps)
              </button>
            </div>

            {/* Refresh */}
            <button
              onClick={() => setLastRefreshed(new Date())}
              className="p-1.5 bg-[#F9FAFB] hover:bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg text-[#6B7280] hover:text-[#1A1A1A] transition-colors cursor-pointer"
              title="Refresh Rate Matrices"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>

            {/* Export CSV */}
            <button
              onClick={handleExportCSV}
              className="p-1.5 bg-[#F9FAFB] hover:bg-[#F3F4F6] border border-[#E5E7EB] rounded-lg text-[#6B7280] hover:text-[#1A1A1A] transition-colors cursor-pointer"
              title="Export Heatmap Matrix as CSV"
            >
              <Download className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>

        {/* Heatmap D3 Container */}
        <div ref={containerRef} className="relative w-full overflow-x-auto pb-1 no-scrollbar sm:overflow-visible">
          <svg ref={svgRef} className="mx-auto block select-none shrink-0" />

          {/* Interactive Floating Tooltip */}
          {hoveredCell && tooltipPos && (
            <div
              className="absolute z-30 pointer-events-none p-3.5 bg-[#1A1A1A] text-white rounded-xl shadow-xl text-xs space-y-2 w-64 animate-in fade-in zoom-in-95 duration-100"
              style={{
                left: Math.min(Math.max(tooltipPos.x - 128, 10), (containerRef.current?.clientWidth || 700) - 270),
                top: Math.max(tooltipPos.y - 140, 10),
              }}
            >
              <div className="flex items-center justify-between border-b border-white/15 pb-1.5">
                <span className="font-extrabold font-mono text-sm tracking-wide text-white">
                  {hoveredCell.base} / {hoveredCell.quote}
                </span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  hoveredCell.volatilityRating === 'High'
                    ? 'bg-rose-500/20 text-rose-300 border border-rose-500/30'
                    : hoveredCell.volatilityRating === 'Elevated'
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                    : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                }`}>
                  {hoveredCell.volatilityRating} Vol
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div>
                  <span className="text-white/60 block text-[10px]">Interbank Rate:</span>
                  <span className="font-mono font-bold text-white">
                    {financialFormat(hoveredCell.rate, hoveredCell.quote)}
                  </span>
                </div>
                <div>
                  <span className="text-white/60 block text-[10px]">24h Volatility:</span>
                  <span className="font-mono font-bold text-amber-300">
                    {hoveredCell.volatilityPct.toFixed(2)}%
                  </span>
                </div>
                <div>
                  <span className="text-white/60 block text-[10px]">24h Range:</span>
                  <span className="font-mono text-[10px] text-white/80">
                    {hoveredCell.low24h.toFixed(4)} - {hoveredCell.high24h.toFixed(4)}
                  </span>
                </div>
                <div>
                  <span className="text-white/60 block text-[10px]">24h Net Change:</span>
                  <span className={`font-mono font-bold ${
                    hoveredCell.change24hPct >= 0 ? 'text-emerald-400' : 'text-rose-400'
                  }`}>
                    {hoveredCell.change24hPct >= 0 ? '+' : ''}{hoveredCell.change24hPct.toFixed(2)}%
                  </span>
                </div>
              </div>

              <div className="pt-1 border-t border-white/10 flex items-center justify-between text-[10px] text-white/60">
                <span>Spread: ~{hoveredCell.spreadBps} bps</span>
                <span className="text-[#60A5FA] flex items-center gap-1 font-semibold">
                  Click to convert <ArrowRight className="w-2.5 h-2.5" />
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Horizontal Scroll Cue */}
        <div className="flex sm:hidden items-center justify-center gap-1.5 py-1 text-[11px] text-[#6B7280]">
          <MoveHorizontal className="w-3.5 h-3.5 text-[#9CA3AF]" />
          <span>Slide horizontally to explore full matrix</span>
        </div>

        {/* Dynamic Color Scale Legend */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-[#F3F4F6] text-xs text-[#6B7280]">
          <div className="flex items-center gap-3">
            <span className="font-bold text-[#1A1A1A]">Legend:</span>
            {metric === 'volatility' && (
              <div className="flex items-center gap-2">
                <span className="text-[11px]">Low (&lt;0.4%)</span>
                <div className="w-32 h-2.5 rounded-full bg-gradient-to-r from-[#F0FDF4] via-[#FEF3C7] to-[#DC2626] border border-[#E5E7EB]" />
                <span className="text-[11px]">High (&gt;1.5%)</span>
              </div>
            )}
            {metric === 'change' && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] text-rose-600">-1.5% Loss</span>
                <div className="w-32 h-2.5 rounded-full bg-gradient-to-r from-[#EF4444] via-[#F8FAFC] to-[#16A34A] border border-[#E5E7EB]" />
                <span className="text-[11px] text-emerald-600">+1.5% Gain</span>
              </div>
            )}
            {metric === 'spread' && (
              <div className="flex items-center gap-2">
                <span className="text-[11px]">Tight (1.2 bps)</span>
                <div className="w-32 h-2.5 rounded-full bg-gradient-to-r from-[#ECFDF5] via-[#C7D2FE] to-[#6366F1] border border-[#E5E7EB]" />
                <span className="text-[11px]">Wide (9.5 bps)</span>
              </div>
            )}
          </div>

          <div className="text-[11px] text-[#9CA3AF]">
            Diagonal cells represent self-paired base assets (1.0000). Powered by institutional FX quotes.
          </div>
        </div>
      </div>
    </div>
  );
};
